import { useEffect, useState } from 'react'
import { CheckCircle, X, Info, Warning } from '@phosphor-icons/react'

export type ToastKind = 'success' | 'info' | 'warning'

export interface ToastPayload {
  id?: string
  kind?: ToastKind
  title: string
  message?: string
  duration?: number
}

// Lightweight global toast: dispatch a CustomEvent('app-toast', { detail: ToastPayload }).
// ToastHost renders the queue in the corner. Avoids pulling a toast lib.

// Module-level buffer for toasts dispatched BEFORE <ToastHost> mounts (e.g. on cold-load
// when a child's mount effect calls toast() before the parent has attached its listener).
// First ToastHost to mount flushes the buffer.
const _pendingToasts: ToastPayload[] = []
let _hostMounted = false

export function toast(payload: ToastPayload): void {
  if (typeof window === 'undefined') return
  if (!_hostMounted) {
    _pendingToasts.push(payload)
  }
  window.dispatchEvent(new CustomEvent('app-toast', { detail: payload }))
}

interface ActiveToast extends ToastPayload {
  id: string
}

export function ToastHost() {
  const [items, setItems] = useState<ActiveToast[]>([])

  useEffect(() => {
    const onToast = (e: Event) => {
      const detail = (e as CustomEvent<ToastPayload>).detail
      if (!detail) return
      const id = detail.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const t: ActiveToast = { ...detail, id, kind: detail.kind || 'success', duration: detail.duration ?? 5000 }
      setItems((prev) => [...prev, t])
      window.setTimeout(() => {
        setItems((prev) => prev.filter((x) => x.id !== id))
      }, t.duration)
    }
    window.addEventListener('app-toast', onToast)
    _hostMounted = true
    // Flush any toasts that fired before this listener was attached (e.g. cold-load child effects).
    if (_pendingToasts.length > 0) {
      const toFlush = _pendingToasts.splice(0, _pendingToasts.length)
      for (const p of toFlush) {
        window.dispatchEvent(new CustomEvent('app-toast', { detail: p }))
      }
    }
    return () => {
      window.removeEventListener('app-toast', onToast)
      _hostMounted = false
    }
  }, [])

  return (
    <div
      data-testid="toast-host"
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm pointer-events-none"
    >
      {items.map((t) => {
        const Icon = t.kind === 'warning' ? Warning : t.kind === 'info' ? Info : CheckCircle
        const accent =
          t.kind === 'warning'
            ? 'bg-primary text-white border-edge'
            : t.kind === 'info'
              ? 'bg-secondary text-white border-edge'
              : 'bg-accent text-ink border-edge'
        return (
          <div
            key={t.id}
            data-testid={`toast-${t.kind}`}
            className={`pointer-events-auto card-brutal p-3 pr-2 flex items-start gap-2 animate-fade-in-up ${accent}`}
          >
            <Icon size={20} weight="fill" className="shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-black text-sm leading-tight">{t.title}</div>
              {t.message && (
                <>
                  {' '}
                  <div className="text-xs opacity-90 mt-0.5 break-words">{t.message}</div>
                </>
              )}
            </div>
            <button
              onClick={() => setItems((prev) => prev.filter((x) => x.id !== t.id))}
              data-testid="toast-dismiss"
              className="p-1 rounded hover:bg-black/10 transition-colors shrink-0"
              aria-label="Dismiss"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
